import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  ImageSquareIcon,
  LinkSimpleIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  TableIcon,
  TextBolderIcon,
  TextItalicIcon,
} from '@phosphor-icons/react';
import { Image } from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { type ReactNode, type RefObject, Suspense, lazy, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { TextSelectionToolbar } from '@/components/ui/text-selection-toolbar';
import { type QuoteLookup, lookupQuote } from '@/lib/documents/review-quote';
import { cn } from '@/lib/utils';

const MediaDrawer = lazy(() => import('@/components/create/studio/MediaDrawer'));

interface ReviewAction {
  quote: string;
  instruction?: string;
  /** One per press of "Fix with AI": the same finding pressed again is a new request. */
  requestId?: number;
}

const reviewActionKey = (action: ReviewAction): string =>
  `${action.quote}:${action.instruction ?? ''}:${action.requestId ?? 0}`;

interface DocumentRichEditorProps {
  markdown: string;
  onChange: (markdown: string) => void;
  disabled?: boolean;
  accessToken: string | null;
  reviewAction?: ReviewAction;
  /** Save state shown at the trailing end of the toolbar ("Unsaved changes"). */
  status?: string | null;
  /** Errors and conflict recovery, shown under the toolbar above the article. */
  feedback?: ReactNode;
}

export interface UnsupportedMarkdownFeature {
  kind:
    | 'html-comment'
    | 'raw-html'
    | 'footnote'
    | 'task-list'
    | 'reference-link'
    | 'frontmatter'
    | 'definition-list'
    | 'mdx';
  label: string;
}

/**
 * Tiptap's Markdown bridge only serializes syntax represented by its schema.
 * These constructs are parsed away, flattened, or rewritten by that schema,
 * so opening them in rich mode would make a no-op save destructive.
 */
export function detectUnsupportedMarkdown(markdown: string): UnsupportedMarkdownFeature[] {
  const rules: Array<{
    kind: UnsupportedMarkdownFeature['kind'];
    label: string;
    test: RegExp;
  }> = [
    { kind: 'html-comment', label: 'HTML comments', test: /<!--|-->/ },
    {
      kind: 'raw-html',
      label: 'raw HTML',
      test: /<!DOCTYPE\s|<\/?[A-Za-z][A-Za-z0-9-]*(?:\s[^>\n]*)?\s*\/?>/i,
    },
    { kind: 'footnote', label: 'footnotes', test: /\[\^[^\]\n]+\]/ },
    { kind: 'task-list', label: 'task lists', test: /^\s{0,3}[-+*]\s+\[[ xX]\]\s+/m },
    {
      kind: 'reference-link',
      label: 'reference-style links',
      test: /^\s{0,3}\[(?!\^)[^\]\n]+\]:\s*\S+/m,
    },
    { kind: 'frontmatter', label: 'frontmatter', test: /^(?:---|\+\+\+)\s*\r?\n/ },
    { kind: 'definition-list', label: 'definition lists', test: /^\s{0,3}:\s+\S+/m },
    { kind: 'mdx', label: 'MDX imports or exports', test: /^(?:import|export)\s+.+$/m },
  ];

  return rules
    .filter((rule) => rule.test.test(markdown))
    .map(({ kind, label }) => ({ kind, label }));
}

export const DOCUMENT_EXTENSIONS = [
  StarterKit.configure({
    link: {
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
    },
  }),
  TableKit.configure({
    table: {
      resizable: true,
      HTMLAttributes: { class: 'mora-document-table' },
    },
  }),
  Image.configure({
    HTMLAttributes: { class: 'mora-document-image' },
  }),
  Markdown.configure({ markedOptions: { gfm: true } }),
];

/** Markdown has no semantic trailing blank paragraph; canonicalize it once. */
export function serializeDocumentMarkdown(editor: Editor): string {
  return editor.getMarkdown().trimEnd();
}

/**
 * Find a quote in ProseMirror's document while preserving its real document
 * positions. Text nodes separated only by marks stay contiguous; block
 * boundaries get a sentinel so a quote can never accidentally bridge two
 * paragraphs that merely happen to touch after `textContent` flattening.
 *
 * A review finding quotes the article's Markdown SOURCE, so the quote may carry link syntax,
 * bold markers or table pipes the document no longer has; `lookupQuote` also matches the plain
 * text such a quote renders to.
 */
export function lookupQuoteInEditor(editor: Editor, quote: string): QuoteLookup {
  if (!quote) return { status: 'missing' };
  let chars = '';
  const positions: Array<number | null> = [];
  let previousTextParent: ProseMirrorNode | null = null;

  editor.state.doc.descendants((node, pos, parent) => {
    if (!node.isText || !node.text) return;
    if (chars && previousTextParent !== parent) {
      chars += '\n';
      positions.push(null);
    }
    for (let index = 0; index < node.text.length; index += 1) {
      chars += node.text[index];
      positions.push(pos + index);
    }
    previousTextParent = parent;
  });

  return lookupQuote({ chars, positions }, quote);
}

export function findUniqueQuoteRange(
  editor: Editor,
  quote: string,
): { from: number; to: number } | null {
  const found = lookupQuoteInEditor(editor, quote);
  return found.status === 'found' ? found.range : null;
}

type ReviewActionState = 'selected' | 'missing' | 'ambiguous';

function ReviewActionNotice({
  action,
  state,
}: {
  action: ReviewAction;
  state: ReviewActionState;
}): JSX.Element {
  if (state === 'selected') {
    return (
      <p role="status" className="text-caption text-muted-foreground">
        {action.instruction
          ? 'The flagged passage is selected and the AI edit request is ready.'
          : 'The flagged passage is selected for editing.'}
      </p>
    );
  }
  return (
    <p role="alert" className="text-caption text-destructive">
      {state === 'ambiguous'
        ? 'The flagged passage appears more than once, so nothing is selected. Select the one you mean.'
        : 'The flagged passage could not be found in the article, so nothing is selected. It may have changed since the review. Select the text yourself, or run the check again.'}
    </p>
  );
}

const REVIEW_PASSAGE_TOAST =
  'The reviewed passage is missing or appears more than once. Refresh the editorial review before editing it.';

/** About half a second of frames: enough for the editor view to mount and take a selection. */
const REVIEW_ACTION_MAX_FRAMES = 30;

/**
 * ProseMirror's own scrollIntoView keeps the selection merely "nearby"; a finding should land in
 * the reader's eye line, so centre the passage in whatever scroll container the editor sits in.
 */
function scrollRangeIntoView(editor: Editor, position: number): void {
  try {
    const { node } = editor.view.domAtPos(position);
    const element = node instanceof Element ? node : node.parentElement;
    element?.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  } catch {
    /* The position was not rendered; the selection itself is already set. */
  }
}

function imageAltFromUrl(url: string): string {
  try {
    const name = decodeURIComponent(new URL(url).pathname.split('/').pop() ?? 'Article image');
    return (
      name
        .replace(/[-_]+/g, ' ')
        .replace(/\.[a-z0-9]+$/i, '')
        .trim() || 'Article image'
    );
  } catch {
    return 'Article image';
  }
}

function EditorToolbar({
  editor,
  onAddImage,
  disabled,
  status,
}: {
  editor: Editor;
  onAddImage: () => void;
  disabled: boolean;
  status?: string | null;
}): JSX.Element {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('https://');

  const setLink = () => {
    if (disabled) return;
    const href = linkHref.trim();
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkOpen(false);
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setLinkOpen(false);
  };

  return (
    <div
      role="toolbar"
      aria-label="Article formatting"
      // Flat, on the page — not a floating pill. It sticks to the reader's
      // scroll region and takes the stage's own colour (`bg-canvas`, the
      // `ARTIFACT_PREVIEW_STAGE` its only caller sits on), so editing reads as
      // writing on the same paper: no raised surface, no second tone, one
      // hairline under the controls. Founder, 2026-10-16: "it should feel like
      // it's on paper instead of this elevated surface … flat, with no color."
      // It spans the whole pane, edge to edge under the header, like the Claude
      // Docs toolbar; inside the 720px article column it read as part of the
      // article (founder, 2026-10-17). `-mx-5` cancels the reader's `px-5`.
      // One row at every width: a narrow screen scrolls it sideways.
      className="no-scrollbar sticky top-0 z-10 -mx-5 mb-10 flex items-center gap-0.5 overflow-x-auto border-b border-border/40 bg-canvas px-3 py-1.5 *:shrink-0"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="flat" size="sm" disabled={disabled}>
            {editor.isActive('heading')
              ? `Heading ${editor.getAttributes('heading').level as number}`
              : 'Paragraph'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            disabled={disabled}
            onSelect={() => editor.chain().focus().setParagraph().run()}
          >
            Paragraph
          </DropdownMenuItem>
          {[1, 2, 3].map((level) => (
            <DropdownMenuItem
              key={level}
              disabled={disabled}
              onSelect={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: level as 1 | 2 | 3 })
                  .run()
              }
            >
              Heading {level}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <span aria-hidden className="mx-1 h-4 w-px bg-border/60" />
      <CircleIconButton
        size="sm"
        ariaLabel="Bold"
        tooltip="Bold"
        aria-pressed={editor.isActive('bold')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <TextBolderIcon weight={editor.isActive('bold') ? 'bold' : 'regular'} />
      </CircleIconButton>
      <CircleIconButton
        size="sm"
        ariaLabel="Italic"
        tooltip="Italic"
        aria-pressed={editor.isActive('italic')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <TextItalicIcon weight={editor.isActive('italic') ? 'bold' : 'regular'} />
      </CircleIconButton>
      <span aria-hidden className="mx-1 h-4 w-px bg-border/60" />
      <CircleIconButton
        size="sm"
        ariaLabel="Bulleted list"
        tooltip="Bulleted list"
        aria-pressed={editor.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListBulletsIcon weight="regular" />
      </CircleIconButton>
      <CircleIconButton
        size="sm"
        ariaLabel="Numbered list"
        tooltip="Numbered list"
        aria-pressed={editor.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListNumbersIcon weight="regular" />
      </CircleIconButton>
      <span aria-hidden className="mx-1 h-4 w-px bg-border/60" />
      <Popover
        open={linkOpen}
        onOpenChange={(open) => {
          if (disabled) return;
          if (open) {
            const previous = editor.getAttributes('link').href as string | undefined;
            setLinkHref(previous ?? 'https://');
          }
          setLinkOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <CircleIconButton size="sm" ariaLabel="Add link" tooltip="Add link" disabled={disabled}>
            <LinkSimpleIcon weight="regular" />
          </CircleIconButton>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-3">
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              setLink();
            }}
          >
            <label htmlFor="document-link-url" className="text-caption font-medium text-foreground">
              Link URL
            </label>
            <Input
              id="document-link-url"
              value={linkHref}
              onChange={(event) => setLinkHref(event.target.value)}
              placeholder="https://example.com"
              autoFocus
              disabled={disabled}
            />
            <div className="flex justify-end gap-2">
              {editor.isActive('link') ? (
                <Button
                  type="button"
                  variant="flat"
                  size="sm"
                  disabled={disabled}
                  onClick={() => {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run();
                    setLinkOpen(false);
                  }}
                >
                  Remove link
                </Button>
              ) : null}
              <Button type="submit" variant="neutral" size="sm" disabled={disabled}>
                Apply link
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
      <CircleIconButton
        size="sm"
        ariaLabel="Insert table"
        tooltip="Insert table"
        disabled={disabled}
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run()}
      >
        <TableIcon weight="regular" />
      </CircleIconButton>
      <CircleIconButton
        size="sm"
        ariaLabel="Insert image"
        tooltip="Insert from library, device, or Google Drive"
        disabled={disabled}
        onClick={onAddImage}
      >
        <ImageSquareIcon weight="regular" />
      </CircleIconButton>
      <span aria-hidden className="mx-1 h-4 w-px bg-border/60" />
      <CircleIconButton
        size="sm"
        ariaLabel="Undo"
        tooltip="Undo"
        disabled={!editor.can().undo()}
        aria-disabled={disabled || !editor.can().undo()}
        {...(disabled ? { disabled: true } : {})}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <ArrowCounterClockwiseIcon weight="regular" />
      </CircleIconButton>
      <CircleIconButton
        size="sm"
        ariaLabel="Redo"
        tooltip="Redo"
        disabled={!editor.can().redo()}
        aria-disabled={disabled || !editor.can().redo()}
        {...(disabled ? { disabled: true } : {})}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <ArrowClockwiseIcon weight="regular" />
      </CircleIconButton>
      {status ? (
        <span role="status" className="ml-auto pl-3 pr-2 text-detail text-muted-foreground">
          {status}
        </span>
      ) : null}
    </div>
  );
}

function RawMarkdownEditor({
  markdown,
  onChange,
  disabled,
  reviewAction,
  status,
  feedback,
  unsupported,
}: Pick<
  DocumentRichEditorProps,
  'markdown' | 'onChange' | 'disabled' | 'reviewAction' | 'status' | 'feedback'
> & {
  unsupported: UnsupportedMarkdownFeature[];
}): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const reviewSelectionRef = useRef<string | null>(null);
  const [reviewNotice, setReviewNotice] = useState<{
    key: string;
    state: ReviewActionState;
  } | null>(null);

  useEffect(() => {
    if (!reviewAction || disabled) return;
    const key = reviewActionKey(reviewAction);
    if (reviewSelectionRef.current === key) return;
    reviewSelectionRef.current = key;
    const found = lookupQuote(
      { chars: markdown, positions: Array.from(markdown, (_, index) => index) },
      reviewAction.quote,
    );
    if (found.status !== 'found') {
      setReviewNotice({ key, state: found.status });
      toast.error(REVIEW_PASSAGE_TOAST);
      return;
    }
    setReviewNotice({ key, state: 'selected' });
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(found.range.from, found.range.to);
    });
  }, [disabled, markdown, reviewAction]);

  return (
    <div className="mx-auto flex w-full max-w-180 flex-col gap-3">
      {status ? (
        <p role="status" className="text-caption text-muted-foreground">
          {status}
        </p>
      ) : null}
      {feedback}
      {reviewAction && reviewNotice?.key === reviewActionKey(reviewAction) ? (
        <ReviewActionNotice action={reviewAction} state={reviewNotice.state} />
      ) : null}
      <RawMarkdownSurface
        markdown={markdown}
        onChange={onChange}
        disabled={disabled}
        unsupported={unsupported}
        textareaRef={textareaRef}
      />
    </div>
  );
}

function RawMarkdownSurface({
  markdown,
  onChange,
  disabled,
  unsupported,
  textareaRef,
}: Pick<DocumentRichEditorProps, 'markdown' | 'onChange' | 'disabled'> & {
  unsupported: UnsupportedMarkdownFeature[];
  textareaRef: RefObject<HTMLTextAreaElement>;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-background shadow-elevation-1">
      <div className="border-b border-border/40 px-4 py-3">
        <p className="text-caption font-medium text-foreground">
          Source mode protects this article
        </p>
        <p className="mt-1 text-detail text-muted-foreground">
          Rich editing could change {unsupported.map((feature) => feature.label).join(', ')}. This
          editor preserves the Markdown exactly.
        </p>
      </div>
      <textarea
        ref={textareaRef}
        aria-label="Edit Markdown source"
        aria-multiline="true"
        value={markdown}
        disabled={disabled}
        spellCheck
        onChange={(event) => onChange(event.target.value)}
        className="min-h-96 w-full resize-y bg-background px-5 py-6 font-mono text-sm leading-6 text-foreground outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
      />
    </div>
  );
}

/**
 * WYSIWYG article editor backed by Mora's canonical Markdown storage. Tiptap's
 * official Markdown extension owns both directions; the database continues to
 * receive Markdown, so downloads, citations and the read renderer keep one
 * source of truth instead of acquiring an HTML shadow format.
 */
function RichDocumentEditor({
  markdown,
  onChange,
  disabled = false,
  accessToken,
  reviewAction,
  status,
  feedback,
}: DocumentRichEditorProps): JSX.Element {
  const [mediaOpen, setMediaOpen] = useState(false);
  const [selection, setSelection] = useState<{ from: number; to: number; text: string } | null>(
    null,
  );
  const lastEmittedRef = useRef(markdown);
  const reviewSelectionRef = useRef<string | null>(null);
  const [reviewNotice, setReviewNotice] = useState<{
    key: string;
    state: ReviewActionState;
  } | null>(null);
  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions: DOCUMENT_EXTENSIONS,
    content: markdown,
    contentType: 'markdown',
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-label': 'Edit article',
        'aria-multiline': 'true',
        // No ring around the whole page: a contenteditable matches
        // `:focus-visible` on every focus, so the old ring framed the entire
        // article for the whole editing session. The caret and the toolbar's
        // pressed states are the focus indicators, as in any document editor.
        class: 'outline-none',
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      const next = serializeDocumentMarkdown(currentEditor);
      lastEmittedRef.current = next;
      onChange(next);
    },
  });

  useEffect(() => {
    if (!editor) return;
    // Without `false` this emits an "update" for a document that did not change, which wrote the
    // editor's re-serialised Markdown (re-padded tables, other bullet marks) into the draft and
    // showed "Unsaved changes" the moment the editor opened.
    if (editor.isEditable !== !disabled) editor.setEditable(!disabled, false);
    if (disabled) {
      setSelection(null);
      setMediaOpen(false);
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    const update = () => {
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, '\n');
      setSelection(from === to || !text.trim() ? null : { from, to, text });
    };
    editor.on('selectionUpdate', update);
    update();
    return () => {
      editor.off('selectionUpdate', update);
    };
  }, [editor]);

  useEffect(() => {
    if (
      !editor ||
      markdown === lastEmittedRef.current ||
      markdown.trimEnd() === serializeDocumentMarkdown(editor)
    )
      return;
    editor.commands.setContent(markdown, { contentType: 'markdown', emitUpdate: false });
    lastEmittedRef.current = markdown;
  }, [editor, markdown]);

  // Applies the pending "Fix with AI" / "Edit passage" request. It waits for the editor to be
  // mounted with the article loaded, selects and scrolls to the passage, and is consumed only once
  // the selection has actually taken. A passage that cannot be found says so instead of nothing.
  useEffect(() => {
    if (!editor || !reviewAction) return;
    const key = reviewActionKey(reviewAction);
    if (reviewSelectionRef.current === key) return;
    let frame = 0;
    let attempts = 0;
    const attempt = () => {
      frame = 0;
      if (editor.isDestroyed) return;
      const loaded = markdown.trim() === '' || editor.state.doc.textContent.length > 0;
      if (!loaded) {
        if ((attempts += 1) < REVIEW_ACTION_MAX_FRAMES) frame = requestAnimationFrame(attempt);
        return;
      }
      const found = lookupQuoteInEditor(editor, reviewAction.quote);
      if (found.status !== 'found') {
        reviewSelectionRef.current = key;
        setReviewNotice({ key, state: found.status });
        toast.error(REVIEW_PASSAGE_TOAST);
        return;
      }
      const { from, to } = found.range;
      editor.chain().focus().setTextSelection({ from, to }).scrollIntoView().run();
      const taken = editor.state.selection.from === from && editor.state.selection.to === to;
      if (!taken && (attempts += 1) < REVIEW_ACTION_MAX_FRAMES) {
        frame = requestAnimationFrame(attempt);
        return;
      }
      reviewSelectionRef.current = key;
      setReviewNotice({ key, state: taken ? 'selected' : 'missing' });
      scrollRangeIntoView(editor, from);
    };
    frame = requestAnimationFrame(attempt);
    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [editor, markdown, reviewAction]);

  if (!editor) {
    return (
      <div className="flex min-h-96 items-center justify-center" role="status">
        <Spinner />
        <span className="sr-only">Loading article editor</span>
      </div>
    );
  }

  return (
    <RichDocumentSurface
      editor={editor}
      markdown={markdown}
      disabled={disabled}
      accessToken={accessToken}
      reviewAction={reviewAction}
      status={status}
      notice={
        <>
          {feedback}
          {reviewAction && reviewNotice?.key === reviewActionKey(reviewAction) ? (
            <ReviewActionNotice action={reviewAction} state={reviewNotice.state} />
          ) : null}
        </>
      }
      selection={selection}
      setSelection={setSelection}
      mediaOpen={mediaOpen}
      setMediaOpen={setMediaOpen}
    />
  );
}

function RichDocumentSurface({
  editor,
  markdown,
  disabled,
  accessToken,
  reviewAction,
  status,
  notice,
  selection,
  setSelection,
  mediaOpen,
  setMediaOpen,
}: Pick<DocumentRichEditorProps, 'markdown' | 'accessToken' | 'reviewAction' | 'status'> & {
  notice: ReactNode;
  editor: Editor;
  disabled: boolean;
  selection: { from: number; to: number; text: string } | null;
  setSelection: (next: { from: number; to: number; text: string } | null) => void;
  mediaOpen: boolean;
  setMediaOpen: (open: boolean) => void;
}): JSX.Element {
  return (
    <div className="relative">
      <EditorToolbar
        editor={editor}
        disabled={disabled}
        status={status}
        onAddImage={() => {
          if (!disabled) setMediaOpen(true);
        }}
      />
      <div
        data-review-highlight-ignore=""
        className="mx-auto mb-6 flex w-full max-w-180 flex-col gap-3 empty:hidden"
      >
        {notice}
      </div>
      {/* The measure lives on a wrapper: `.mora-article` is unlayered and sets
          `max-width: none`, which beats a `max-w-*` utility on the same node. */}
      <div className="mx-auto w-full max-w-180">
        <EditorContent
          editor={editor}
          // `.mora-article` (src/styles/article-document.css) is the SAME type
          // system the reader uses, so editing shows the article as it reads:
          // serif title and headings, 18px body, hairline tables.
          className={cn(
            'mora-article mora-article-enter min-h-96 transition-opacity duration-200 ease-out',
            // Centred H1 matches the reader's headline (founder, 2026-09-07).
            '[&_.tiptap]:min-h-96 [&_.tiptap]:outline-none [&_.tiptap_h1]:text-center',
            disabled && 'opacity-60',
          )}
        />
      </div>
      {/!\[[^\]]*\]\([^\n)]+(?:\s+"[^"]*")?\)/.test(markdown) ? (
        <p className="mx-auto mt-8 max-w-180 border-t border-border/30 pt-3 text-detail text-muted-foreground">
          Images stay linked in Mora and Markdown. Word and PDF currently list their alt text until
          article image packaging is complete.
        </p>
      ) : null}
      {selection && !disabled ? (
        <TextSelectionToolbar
          key={`${selection.from}:${selection.to}:${reviewAction ? reviewActionKey(reviewAction) : ''}`}
          getAnchorRect={() => {
            const start = editor.view.coordsAtPos(selection.from);
            const end = editor.view.coordsAtPos(selection.to);
            return new DOMRect(
              Math.min(start.left, end.left),
              Math.min(start.top, end.top),
              Math.max(start.right, end.right) - Math.min(start.left, end.left),
              Math.max(start.bottom, end.bottom) - Math.min(start.top, end.top),
            );
          }}
          selectedText={selection.text}
          context={serializeDocumentMarkdown(editor)}
          accessToken={accessToken}
          useBrandVoice
          allowFreeform
          confirmRewrite
          initialInstruction={reviewAction?.instruction}
          onApply={(replacement) => {
            editor
              .chain()
              .focus()
              .setTextSelection({ from: selection.from, to: selection.to })
              .insertContent(replacement)
              .run();
            setSelection(null);
          }}
          onDismiss={() => {
            editor.commands.setTextSelection(selection.to);
            setSelection(null);
          }}
        />
      ) : null}
      <Suspense fallback={null}>
        <MediaDrawer
          isOpen={mediaOpen && !disabled}
          onClose={() => setMediaOpen(false)}
          mediaKind="image"
          onSelect={(url) => {
            editor.commands.setImage({
              src: url,
              alt: imageAltFromUrl(url),
              title: imageAltFromUrl(url),
            });
            setMediaOpen(false);
          }}
        />
      </Suspense>
    </div>
  );
}

export function DocumentRichEditor(props: DocumentRichEditorProps): JSX.Element {
  const unsupported = detectUnsupportedMarkdown(props.markdown);
  if (unsupported.length > 0) {
    return <RawMarkdownEditor {...props} unsupported={unsupported} />;
  }
  return <RichDocumentEditor {...props} />;
}
